/**
 * Words written in place on the board (an item's, a frame's title): shown as text, and while
 * `editing` a field over them that takes the keyboard, with every word selected (a double click,
 * Miro's) or the caret where the words were clicked (`at`, a page point). Leaving the field (a
 * click elsewhere, Escape, Ctrl+Enter) sends what was written; Escape on unchanged words just leaves.
 *
 * An item's words may have looks of their own (`rich`: its `style.rich`, `shared/richText.ts`):
 * shown as styled runs, and while written the field is the board's {@link textEditor}, so the
 * style bar changes only the words selected. Pressing a field of the style bar (the size, the
 * font) keeps the words open: they close when the keyboard goes anywhere else.
 */
import { For, Show, createEffect, createSignal, on, onCleanup, untrack, type JSX } from "solid-js";
import type { Style } from "../../../shared/elements";
import { hasOwnLooks, plainOf, pushDown, rangeValue, readRich, runCss, styleRange, withoutKey, type Run, type RunKey } from "../../../shared/richText";
import { insertText, renderRuns, runsOf, select, selectionIn, useTextEditor, type Span, type TextEditor } from "./textEditor";

/** A collapsed range at page point (`x`, `y`) in the words there (`null`: none, or the browser cannot tell). */
function caretAt(x: number, y: number): Range | null {
  const doc = document as Document & { caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null; caretRangeFromPoint?: (x: number, y: number) => Range | null };
  if (doc.caretPositionFromPoint) {
    const p = doc.caretPositionFromPoint(x, y);
    if (!p) return null;
    const range = document.createRange();
    range.setStart(p.offsetNode, p.offset);
    return range;
  }
  return doc.caretRangeFromPoint?.(x, y) ?? null;
}

/** The keys (with Ctrl) that change the selected words' look, and how. */
const FORMAT_KEYS: Record<string, [RunKey, string]> = { b: ["weight", "bold"], i: ["italic", "yes"], u: ["underline", "yes"], "x+shift": ["strike", "yes"] };

/** Whether `el` is in the board's style bar. */
const inStyleBar = (el: EventTarget | null) => el instanceof Element && !!el.closest("#board-stylebar");

/**
 * Text, or a field writing it. With `id` and `writeRich`, the words may have looks of their own
 * (`rich`, over the item's `whole` style): `writeRich(runs, cleared)` sends them with the whole
 * keys moved down into the words.
 */
export function Editable(props: {
  text: string;
  editing: boolean;
  done: () => void;
  write: (text: string) => void;
  class?: string;
  at?: { x: number; y: number } | null;
  id?: string;
  rich?: string;
  whole?: Style;
  writeRich?: (runs: readonly Run[], cleared: string[]) => void;
}): JSX.Element {
  let field: HTMLDivElement | undefined;
  /** A field of the style bar has the keyboard: the words stay open. */
  let parked = false;
  /** The field as the style bar sees it, while open with looks of its own. */
  let editor: TextEditor | undefined;
  const runs = () => readRich(props.rich, props.text);
  const [selection, setSelection] = createSignal<Span | null>(null);

  /** Sends the words and leaves the field. */
  const finish = () => {
    if (!field) return;
    parked = false;
    const now = runsOf(field);
    props.done();
    if (props.writeRich) return props.writeRich(now, []);
    const text = plainOf(now);
    if (text !== props.text) props.write(text);
  };

  createEffect(
    on(
      () => props.editing,
      (editing) => {
        if (!editing || !field) return;
        const el = field;
        untrack(() => renderRuns(el, runs()));
        el.focus();
        // The caret where the words were clicked, else everything selected (a double click into a note in Miro).
        const at = props.at ? caretAt(props.at.x, props.at.y) : null;
        const range = at && el.contains(at.startContainer) ? at : document.createRange();
        if (range !== at) range.selectNodeContents(el);
        getSelection()?.removeAllRanges();
        getSelection()?.addRange(range);
        setSelection(selectionIn(el));
        const watch = () => {
          const s = selectionIn(el);
          if (s) setSelection(s);
        };
        // Leaving a field of the style bar for anything but the words closes them.
        const left = (e: FocusEvent) => {
          if (!parked || !inStyleBar(e.target)) return;
          if (e.relatedTarget === el) return void (parked = false);
          if (inStyleBar(e.relatedTarget)) return;
          setTimeout(() => parked && document.activeElement !== el && finish());
        };
        document.addEventListener("selectionchange", watch);
        document.addEventListener("focusout", left);
        let mine: TextEditor | undefined;
        if (props.id && props.writeRich) {
          /** The selected words' new look, written into the field and sent. */
          const change = (key: RunKey, value: string, all = false) => {
            const s = all ? { start: 0, end: plainOf(runsOf(el)).length } : selection();
            if (!s || s.start === s.end) return;
            let now = runsOf(el);
            const cleared: string[] = [];
            const whole = props.whole?.[key];
            // The item's own look for this key goes down into its words, so some can be without it.
            if (whole && key !== "highlight") {
              now = pushDown(now, key, whole);
              cleared.push(key);
            }
            now = styleRange(now, s.start, s.end, key, value);
            renderRuns(el, now);
            el.focus();
            select(el, all ? selection() ?? s : s);
            parked = false;
            props.writeRich?.(now, cleared);
          };
          mine = {
            id: props.id,
            selection,
            value: (key) => {
              const s = selection();
              return s ? rangeValue(runsOf(el), s.start, s.end, key, props.whole) : (props.whole?.[key] ?? "");
            },
            apply: change,
            clear: (key) => {
              const s = selection();
              renderRuns(el, withoutKey(runsOf(el), key));
              // Selecting words would take the keyboard back from a field of the style bar.
              if (s && document.activeElement === el) select(el, s);
            },
          };
          useTextEditor(mine);
          editor = mine;
        }
        onCleanup(() => {
          document.removeEventListener("selectionchange", watch);
          document.removeEventListener("focusout", left);
          if (mine) useTextEditor(null, mine);
          editor = undefined;
          parked = false;
        });
      },
    ),
  );

  return (
    <Show
      when={props.editing}
      fallback={
        <div class={`board-words ${props.class ?? ""}`}>
          <Show when={hasOwnLooks(runs())} fallback={props.text}>
            <For each={runs()}>{(r) => <span style={runCss(r.style)}>{r.text}</span>}</For>
          </Show>
        </div>
      }
    >
      <div
        ref={field}
        class={`board-words editing ${props.class ?? ""}`}
        id="board-text"
        contentEditable
        onPointerDown={(e) => e.stopPropagation()}
        onDblClick={(e) => e.stopPropagation()}
        onBlur={(e) => {
          if (inStyleBar(e.relatedTarget)) return void (parked = true);
          finish();
        }}
        onPaste={(e) => {
          // Only the words come in: their look is the item's.
          e.preventDefault();
          if (field) insertText(field, e.clipboardData?.getData("text/plain") ?? "");
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Escape" || (e.key === "Enter" && (e.ctrlKey || e.metaKey))) {
            e.preventDefault();
            field?.blur();
          } else if ((e.ctrlKey || e.metaKey) && !e.altKey && FORMAT_KEYS[e.key.toLowerCase() + (e.shiftKey ? "+shift" : "")]) {
            // The browser's own bold, italic and underline write tags this field does not keep: the selected words' look instead.
            e.preventDefault();
            const [key, value] = FORMAT_KEYS[e.key.toLowerCase() + (e.shiftKey ? "+shift" : "")]!;
            if (editor) editor.apply(key, editor.value(key) === value ? "" : value);
          } else if (e.key === "Enter" && field) {
            // A line break as a character, the same in every browser.
            e.preventDefault();
            insertText(field, "\n");
          }
        }}
      />
    </Show>
  );
}
